import { decideUpdatePrompt, storeUrlFor } from './appUpdateConfig';
import type { AppVersionInfo } from './appVersion';

// =============================================================================
// Optional nudges — one coordinated model for every voluntary in-app prompt:
//   photo         "add a profile photo"            (highest priority)
//   verification  "get Vibe Verified" / "try again"
//   survey        7-day feedback survey
//   review        official store-review API call  (no custom UI, ever)
// Pure functions over a per-user NudgeState (Firestore nudgeState/{uid}, also
// read by functions/optionalReminders.js for push cadence) and an injected
// `now`, so every timing rule is unit-tested. Critical notices (account,
// required/recommended updates) are never decided here — they win by simply
// being on screen (see nudgeSession.ts overlays).
// =============================================================================

export const DAY_MS = 24 * 60 * 60 * 1000;

export const NUDGE_RULES = Object.freeze({
  sessionsPerPopup: 1, // at most one optional popup per app session
  sharedCooldownMs: 3 * DAY_MS, // between any two optional popups
  calmDwellMs: 20_000, // continuous time on a calm screen before a popup
  pushQuietMs: 12 * 60 * 60 * 1000, // no popup this soon after opening a reminder push
  exploreMinMs: 3 * DAY_MS, // profile/verification popups wait until people have explored
  exploreMinActiveDays: 2,
  profileSnoozeMs: 5 * DAY_MS, // after "Not now"
  profileMaxDismissals: 3, // then automatic prompts stop (Profile keeps both actions)
  surveyMinUseMs: 7 * DAY_MS,
  surveyRemindMs: 7 * DAY_MS, // "Remind me later"
  surveyMaxRemindLaters: 2, // a third "later" is treated as "don't ask again"
  postUpdateDelayMs: 7 * DAY_MS, // survey opens 7 days after an update is observed
  reviewMinUseMs: 21 * DAY_MS,
  reviewMinActiveDays: 8,
  reviewAwayFromSurveyMs: 14 * DAY_MS, // never right after survey questions (Google policy)
  reviewIntervalMs: 120 * DAY_MS, // our cap on top of the OS quotas
});

export type NudgeKind = 'photo' | 'verification' | 'survey' | 'review';
export type VerificationStatus = 'not_started' | 'pending' | 'approved' | 'rejected';

export type KindState = {
  shown?: number;
  dismissals?: number;
  lastShownAt?: number | null;
  lastDismissedAt?: number | null;
};

export type SurveyState = {
  shown?: number;
  lastShownAt?: number | null;
  remindLaters?: number;
  remindAfter?: number | null;
  dontAskAgain?: boolean;
  pendingUpdateTarget?: string | null; // outdated build seen; survey paused until updated
  storeOpenedAt?: number | null;
  updateObservedAt?: number | null; // the app itself saw the updated build
  eligibleAfter?: number | null; // updateObservedAt + 7 days
  submittedAt?: number | null;
};

export type ReviewState = {
  requests?: number;
  lastRequestedAt?: number | null; // we asked the OS; we never know if a review was written
  lastRequestedVersion?: string | null;
  lastPushOpenedAt?: number | null;
};

export type NudgeState = {
  firstSeenAt?: number | null;
  activeDays?: number;
  lastActiveDay?: string | null;
  lastPopupAt?: number | null;
  lastPopupKind?: NudgeKind | null;
  lastPushOpenedAt?: number | null;
  lastPushOpenedKind?: string | null;
  photo?: KindState;
  verification?: KindState;
  review?: ReviewState;
  surveys?: Record<string, SurveyState>;
};

export type ProfileFacts = {
  hasPhoto: boolean;
  vibeVerified: boolean;
  verificationStatus?: VerificationStatus;
};

export type SurveyConfig = { active?: boolean; surveyId?: string; storeReviewActive?: boolean } | null;

const finite = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/**
 * Earliest trustworthy "started using Tonyte" instant: the Firebase Auth
 * account creation time, the profile's createdAt, or (when neither exists)
 * the first time this app saw the user (state.firstSeenAt). Future values are
 * ignored. null = nothing yet — the caller starts tracking now.
 */
export function firstUseMillis(input: { authCreatedAt?: number | null; profileCreatedAt?: number | null; state?: NudgeState | null }, now: number): number | null {
  const candidates = [input.authCreatedAt, input.profileCreatedAt, input.state?.firstSeenAt]
    .map(finite)
    .filter((value): value is number => value !== null && value <= now);
  return candidates.length ? Math.min(...candidates) : null;
}

/** Patch that counts today as an active day (and starts first-use tracking), or null. */
export function activeDayPatch(state: NudgeState | null, dayKey: string, now: number): Partial<NudgeState> | null {
  if (state?.lastActiveDay === dayKey && finite(state?.firstSeenAt) !== null) return null;
  const patch: Partial<NudgeState> = {};
  if (finite(state?.firstSeenAt) === null) patch.firstSeenAt = now;
  if (state?.lastActiveDay !== dayKey) {
    patch.lastActiveDay = dayKey;
    patch.activeDays = (state?.activeDays || 0) + 1;
  }
  return patch;
}

// ── Profile photo / Vibe Verified ───────────────────────────────────────────

/** Which verification nudge fits the current status (null = none). */
export function verificationNudgeVariant(facts: ProfileFacts): 'start' | 'retry' | null {
  if (!facts.hasPhoto) return null; // the photo reminder goes first
  if (facts.vibeVerified || facts.verificationStatus === 'approved') return null;
  if (facts.verificationStatus === 'pending') return null; // never "start again" while in review
  if (facts.verificationStatus === 'rejected') return 'retry'; // also covers a revoked badge
  return 'start';
}

export function profileNudgeDecision(
  kind: 'photo' | 'verification',
  state: NudgeState | null,
  firstUse: number | null,
  now: number,
): { eligible: boolean; reason: string; until?: number } {
  const kindState = state?.[kind] || {};
  if ((kindState.dismissals || 0) >= NUDGE_RULES.profileMaxDismissals) return { eligible: false, reason: 'dismissed_repeatedly' };
  const dismissedAt = finite(kindState.lastDismissedAt);
  if (dismissedAt !== null && now - dismissedAt < NUDGE_RULES.profileSnoozeMs) {
    return { eligible: false, reason: 'snoozed', until: dismissedAt + NUDGE_RULES.profileSnoozeMs };
  }
  if (firstUse === null || now - firstUse < NUDGE_RULES.exploreMinMs) {
    return { eligible: false, reason: 'still_exploring', until: firstUse === null ? undefined : firstUse + NUDGE_RULES.exploreMinMs };
  }
  if ((state?.activeDays || 0) < NUDGE_RULES.exploreMinActiveDays) return { eligible: false, reason: 'still_exploring' };
  return { eligible: true, reason: 'eligible' };
}

// ── Feedback survey ─────────────────────────────────────────────────────────

export type SurveyStatus =
  | 'eligible' | 'inactive' | 'submitted' | 'dont_ask' | 'too_new' | 'reminder' | 'awaiting_update' | 'post_update_wait';

export function surveyDecision(input: {
  config: SurveyConfig;
  survey: SurveyState | null | undefined;
  firstUse: number | null;
  now: number;
  native: boolean;
  manual?: boolean; // opened from Settings: ignores reminders / "don't ask again"
}): { status: SurveyStatus; until?: number } {
  const { config, survey, firstUse, now } = input;
  if (config?.active !== true || !config.surveyId) return { status: 'inactive' };
  if (finite(survey?.submittedAt) !== null) return { status: 'submitted' };
  const eligibleAfter = finite(survey?.eligibleAfter);
  if (eligibleAfter !== null && now < eligibleAfter) return { status: 'post_update_wait', until: eligibleAfter };
  if (input.native && survey?.pendingUpdateTarget) return { status: 'awaiting_update' };
  if (firstUse === null || now - firstUse < NUDGE_RULES.surveyMinUseMs) {
    return { status: 'too_new', until: firstUse === null ? undefined : firstUse + NUDGE_RULES.surveyMinUseMs };
  }
  if (!input.manual) {
    if (survey?.dontAskAgain) return { status: 'dont_ask' };
    const remindAfter = finite(survey?.remindAfter);
    if (remindAfter !== null && now < remindAfter) return { status: 'reminder', until: remindAfter };
  }
  return { status: 'eligible' };
}

export type VersionCheck = {
  status: 'current' | 'outdated' | 'unknown';
  target?: string;
  targetLabel?: string;
  storeUrl?: string;
};

/**
 * Installed build vs appConfig/update. `config` must come from the server
 * (not a cache) to be trusted as "current"; missing config or version info
 * is 'unknown' — never silently treated as up to date.
 */
export function evaluateInstalledVersion(
  config: Record<string, unknown> | null,
  installed: Pick<AppVersionInfo, 'platform' | 'versionCode' | 'versionName'> | null,
): VersionCheck {
  if (!config || !installed) return { status: 'unknown' };
  if (installed.platform === 'ios' && !installed.versionName) return { status: 'unknown' };
  const decision = decideUpdatePrompt(config, installed);
  if (decision) {
    return {
      status: 'outdated',
      target: decision.target,
      targetLabel: decision.targetLabel || decision.target,
      storeUrl: storeUrlFor(config, installed.platform),
    };
  }
  return { status: 'current' };
}

/**
 * After an outdated build paused the survey: once the app *observes* a
 * current build, record it and open the survey 7 days later. Opening the
 * store link alone never counts.
 */
export function observedUpdatePatch(survey: SurveyState | null | undefined, check: VersionCheck, now: number): Partial<SurveyState> | null {
  if (!survey?.pendingUpdateTarget || check.status !== 'current') return null;
  return { pendingUpdateTarget: null, updateObservedAt: now, eligibleAfter: now + NUDGE_RULES.postUpdateDelayMs };
}

export function surveyRemindLaterPatch(survey: SurveyState | null | undefined, now: number): Partial<SurveyState> {
  const remindLaters = (survey?.remindLaters || 0) + 1;
  return remindLaters > NUDGE_RULES.surveyMaxRemindLaters
    ? { remindLaters, dontAskAgain: true, remindAfter: null }
    : { remindLaters, remindAfter: now + NUDGE_RULES.surveyRemindMs };
}

// ── Store review (official API only) ────────────────────────────────────────

/**
 * Neutral engagement criteria, identical for everyone. Deliberately takes no
 * survey rating: the private survey answer never influences review requests.
 */
export function reviewRequestDecision(input: {
  config: SurveyConfig;
  state: NudgeState | null;
  firstUse: number | null;
  now: number;
  platform: string;
  appVersion: string | null;
}): { eligible: boolean; reason: string } {
  const { config, state, firstUse, now } = input;
  if (config?.storeReviewActive !== true) return { eligible: false, reason: 'inactive' };
  if (input.platform !== 'ios' && input.platform !== 'android') return { eligible: false, reason: 'unsupported_platform' };
  if (firstUse === null || now - firstUse < NUDGE_RULES.reviewMinUseMs) return { eligible: false, reason: 'too_new' };
  if ((state?.activeDays || 0) < NUDGE_RULES.reviewMinActiveDays) return { eligible: false, reason: 'not_enough_use' };
  const nearSurvey = Object.values(state?.surveys || {}).some(survey => [survey.lastShownAt, survey.submittedAt]
    .map(finite).some(at => at !== null && now - at < NUDGE_RULES.reviewAwayFromSurveyMs));
  if (nearSurvey) return { eligible: false, reason: 'near_survey' };
  const last = finite(state?.review?.lastRequestedAt);
  if (last !== null && now - last < NUDGE_RULES.reviewIntervalMs) return { eligible: false, reason: 'requested_recently' };
  if (input.appVersion && state?.review?.lastRequestedVersion === input.appVersion) return { eligible: false, reason: 'same_version' };
  return { eligible: true, reason: 'eligible' };
}

// ── Coordination ─────────────────────────────────────────────────────────────

export type StartupNudge =
  | { kind: 'photo'; variant: number }
  | { kind: 'verification'; mode: 'start' | 'retry'; variant: number }
  | { kind: 'survey'; variant: number }
  | { kind: 'review' };

/**
 * The single optional popup for this session (or why none): one per session,
 * none right after a reminder push was opened, a shared cooldown across all
 * kinds, then priority photo > verification > survey > review.
 */
export function pickStartupNudge(input: {
  now: number;
  sessionUsed: boolean;
  openedFromReminderPush: boolean;
  state: NudgeState | null;
  facts: ProfileFacts | null;
  firstUse: number | null;
  survey: { status: SurveyStatus };
  review: { eligible: boolean };
  variants: { photo: number; verificationStart: number; verificationRetry: number; survey: number };
}): { nudge: StartupNudge | null; reason: string } {
  const { now, state } = input;
  if (input.sessionUsed) return { nudge: null, reason: 'session_used' };
  const pushOpenedAt = finite(state?.lastPushOpenedAt);
  if (input.openedFromReminderPush || (pushOpenedAt !== null && now - pushOpenedAt < NUDGE_RULES.pushQuietMs)) {
    return { nudge: null, reason: 'after_reminder_push' };
  }
  const lastPopupAt = finite(state?.lastPopupAt);
  if (lastPopupAt !== null && now - lastPopupAt < NUDGE_RULES.sharedCooldownMs) return { nudge: null, reason: 'shared_cooldown' };

  const facts = input.facts;
  if (facts && !facts.hasPhoto && profileNudgeDecision('photo', state, input.firstUse, now).eligible) {
    return { nudge: { kind: 'photo', variant: rotate(state?.photo?.shown, input.variants.photo) }, reason: 'photo' };
  }
  const mode = facts ? verificationNudgeVariant(facts) : null;
  if (mode && profileNudgeDecision('verification', state, input.firstUse, now).eligible) {
    const count = mode === 'retry' ? input.variants.verificationRetry : input.variants.verificationStart;
    return { nudge: { kind: 'verification', mode, variant: rotate(state?.verification?.shown, count) }, reason: 'verification' };
  }
  if (input.survey.status === 'eligible') {
    const shown = Object.values(state?.surveys || {}).reduce((sum, survey) => sum + (survey.shown || 0), 0);
    return { nudge: { kind: 'survey', variant: rotate(shown, input.variants.survey) }, reason: 'survey' };
  }
  if (input.review.eligible) return { nudge: { kind: 'review' }, reason: 'review' };
  return { nudge: null, reason: 'nothing_eligible' };
}

/** Round-robin copy: the n-th time a kind is shown uses variant n mod count. */
export function rotate(shownCount: number | undefined, variantCount: number): number {
  if (variantCount <= 1) return 0;
  return (shownCount || 0) % variantCount;
}

/** State patch for a popup that is now on screen (starts the shared cooldown). */
export function popupShownPatch(kind: NudgeKind, state: NudgeState | null, now: number, surveyId?: string): Partial<NudgeState> {
  const base: Partial<NudgeState> = { lastPopupAt: now, lastPopupKind: kind };
  if (kind === 'photo' || kind === 'verification') {
    return { ...base, [kind]: { shown: (state?.[kind]?.shown || 0) + 1, lastShownAt: now } };
  }
  if (kind === 'survey' && surveyId) {
    const survey = state?.surveys?.[surveyId];
    return { ...base, surveys: { [surveyId]: { shown: (survey?.shown || 0) + 1, lastShownAt: now } } };
  }
  return base;
}

export function profileDismissPatch(kind: 'photo' | 'verification', state: NudgeState | null, now: number): Partial<NudgeState> {
  return { [kind]: { dismissals: (state?.[kind]?.dismissals || 0) + 1, lastDismissedAt: now } };
}
